// 1. React
import { memo, useMemo } from "react";

// 2. Helpers
import { formatCurrency, useTranslate } from "helpers";
import { InvoiceOutgoingItemRow } from "services/api/fleet/vehicles/invoices/types";
import { InvoiceTotalProps } from "./types";
import { DiscountType } from "services/api/settings";




const InvoiceTotal: React.FC<InvoiceTotalProps> = (props) => {
    const { formValues, setFormValues } = props;

    const { t } = useTranslate();

    const items = formValues.items.filter(i => !i.deleted);

    const totals = useMemo(() => {
        let totalWithoutTax = 0;
        let totalTax = 0;
        let totalWithTax = 0;

        items?.forEach((item: InvoiceOutgoingItemRow) => {
            const quantity = item.quantity ?? 0;
            const priceWithoutTax = item.priceWithoutTax ?? 0;
            const taxRate = item.taxRate ?? 0;
            const discountAmount = item.discountAmount ?? 0;
            const discountTypeId = item.discountTypeId;

            const baseAmount = quantity * priceWithoutTax;

            let discountedBase = baseAmount;

            // DISCOUNT LOGIC
            if (discountAmount > 0) {
                if (discountTypeId === DiscountType.PERCENTAGE.key) {
                    discountedBase =
                        baseAmount - (baseAmount * discountAmount) / 100;
                }

                if (discountTypeId === DiscountType.AMOUNT.key) {
                    discountedBase = baseAmount - discountAmount;
                }
            }

            // ne dopuštamo negativne iznose
            discountedBase = Math.max(0, discountedBase);

            const taxAmount = (discountedBase * taxRate) / 100;
            const totalAmount = discountedBase + taxAmount;

            totalWithoutTax += discountedBase;
            totalTax += taxAmount;
            totalWithTax += totalAmount;
        });

        return {
            totalWithoutTax: Number(totalWithoutTax.toFixed(2)),
            totalTax: Number(totalTax.toFixed(2)),
            totalWithTax: Number(totalWithTax.toFixed(2)),
        };
    }, [formValues.items]);

    const taxTotalsByRate = useMemo(() => {
        const map: Record<number, number> = {};

        items?.forEach(item => {
            const quantity = item.quantity ?? 0;
            const priceWithoutTax = item.priceWithoutTax ?? 0;
            const taxRate = item.taxRate ?? 0;
            const discountAmount = item.discountAmount ?? 0;
            const discountTypeId = item.discountTypeId;

            const baseAmount = quantity * priceWithoutTax;

            let discountedBase = baseAmount;

            if (discountAmount > 0) {
                if (discountTypeId === DiscountType.PERCENTAGE.key) {
                    discountedBase =
                        baseAmount - (baseAmount * discountAmount) / 100;
                }

                if (discountTypeId === DiscountType.AMOUNT.key) {
                    discountedBase = baseAmount - discountAmount;
                }
            }

            discountedBase = Math.max(0, discountedBase);

            const taxAmount = (discountedBase * taxRate) / 100;

            if (!map[taxRate]) {
                map[taxRate] = 0;
            }

            map[taxRate] += taxAmount;
        });

        return Object.entries(map)
            .map(([rate, amount]) => ({
                taxRate: Number(rate),
                taxAmount: Number(amount.toFixed(2)),
            }))
            .sort((a, b) => a.taxRate - b.taxRate);
    }, [formValues.items]);


    return (
        <>
            {items && items.length > 0 && (
                <div className="">
                    <div className="flex justify-end">
                        <div className="w-[400px] px-3 pb-3.5">
                            <div className="py-2.5 space-y-1.5 bg-white rounded-t-sm border border-slate-200 overflow-hidden">
                                <div className="flex justify-between text-sm text-slate-600 px-4">
                                    <span>Ukupno bez PDV-a:</span>
                                    <span className="font-medium tabular-nums">{formatCurrency.format(totals.totalWithoutTax)}</span>
                                </div>

                                {taxTotalsByRate.map(({ taxRate, taxAmount }) => (
                                    <div key={taxRate} className="flex justify-between text-sm text-slate-600 px-4">
                                        <span>PDV {taxRate}%:</span>
                                        <span className="font-medium tabular-nums">{formatCurrency.format(taxAmount)}</span>
                                    </div>
                                ))}

                                <div className="flex justify-between text-sm text-slate-900 font-medium pt-1.5 border-t border-slate-200 px-4">
                                    <span>Ukupno s PDV-om:</span>
                                    <span className="tabular-nums">{formatCurrency.format(totals.totalWithTax)}</span>
                                </div>
                            </div>

                            <div className="px-4 pt-2 pb-2.5 bg-sky-50 border-x border-b border-slate-200 flex justify-between items-center rounded-b-sm">
                                <span className="text-sm font-semibold text-sky-800">Za plaćanje:</span>
                                <span className="text-md font-semibold text-sky-700 tabular-nums">{formatCurrency.format(totals.totalWithTax)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default memo(InvoiceTotal);
